import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  Plus, Edit2, Trash2, GripVertical, Save, X, Star, Package,
  DollarSign, Clock, MessageSquare, Code, Palette, Camera,
  FileText, Megaphone, Settings, Zap, Globe, Shield, ChevronDown, ChevronUp, Eye, EyeOff,
  Image as ImageIcon, Upload
} from 'lucide-react';
import api from '../../../services/api';
import { PreviewContext } from '../PreviewContext';
import { buildMediaUrl } from '../../../utils/mediaUrl';
import BackgroundPicker from '../Business/BackgroundPicker';

const theme = {
  bgPrimary: '#0f172a',
  bgSecondary: '#1e293b',
  bgTertiary: '#334155',
  border: '#334155',
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
  accent: '#6366f1',
  accentHover: '#4f46e5',
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
};

const ICON_OPTIONS = [
  { name: 'Code', icon: Code, label: 'Development' },
  { name: 'Palette', icon: Palette, label: 'Design' },
  { name: 'Camera', icon: Camera, label: 'Photography' },
  { name: 'FileText', icon: FileText, label: 'Writing' },
  { name: 'Megaphone', icon: Megaphone, label: 'Marketing' },
  { name: 'Settings', icon: Settings, label: 'Consulting' },
  { name: 'Zap', icon: Zap, label: 'Performance' },
  { name: 'Globe', icon: Globe, label: 'Web' },
  { name: 'Shield', icon: Shield, label: 'Security' },
  { name: 'Package', icon: Package, label: 'Product' },
];

const PRICE_TYPES = [
  { value: 'hourly', label: 'Per Hour', prefix: '', suffix: '/hr' },
  { value: 'fixed', label: 'Fixed Price', prefix: '', suffix: '' },
  { value: 'starting', label: 'Starting At', prefix: 'From ', suffix: '' },
  { value: 'contact', label: 'Contact for Quote', prefix: '', suffix: '' },
];


const AdminServices = () => {
  const { updateLiveServices } = useContext(PreviewContext);
  const [services, setServices] = useState([]);
  const [sectionSettings, setSectionSettings] = useState({
    title: 'Services',
    subtitle: '',
    cards_per_row: 3,
    show_images: true,
    show_prices: true,
    show_icons: true,
    show_description: true,
    background_color: '',
    text_color: '',
    card_background_color: '',
    card_text_color: '',
  });
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const formRef = useRef(null);
  const settingsSaveTimeoutRef = useRef(null);

  const emptyService = {
    title: '',
    description: '',
    short_description: '',
    icon: 'Code',
    image: null,
    price: '',
    price_type: 'hourly',
    is_featured: false,
    is_active: true,
  };

  const [formData, setFormData] = useState(emptyService);

  useEffect(() => {
    fetchData();
  }, []);

  const defaultSettings = {
    title: 'Services',
    subtitle: '',
    cards_per_row: 3,
    show_images: true,
    show_prices: true,
    show_icons: true,
    show_description: true,
  };

  const fetchData = async () => {
    try {
      const [servicesRes, sectionRes] = await Promise.all([
        api.get('/services/'),
        api.get('/services/section/').catch(() => ({ data: null }))
      ]);
      setServices(servicesRes.data);
      // Merge backend data with defaults to ensure all fields have values
      const settings = { ...defaultSettings, ...(sectionRes.data || {}) };
      setSectionSettings(settings);
      updateLiveServices(servicesRes.data, settings);
      // Allow auto-save after data loads
      setTimeout(() => setSettingsLoaded(true), 500);
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update preview whenever section settings change
  useEffect(() => {
    updateLiveServices(services, sectionSettings);
  }, [sectionSettings, services, updateLiveServices]);

  // Auto-save section settings with debounce
  useEffect(() => {
    if (!settingsLoaded) return;

    if (settingsSaveTimeoutRef.current) {
      clearTimeout(settingsSaveTimeoutRef.current);
    }

    settingsSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await api.put('/services/section/', sectionSettings);
      } catch (err) {
        console.error('Error auto-saving section settings:', err);
      }
    }, 800);

    return () => {
      if (settingsSaveTimeoutRef.current) {
        clearTimeout(settingsSaveTimeoutRef.current);
      }
    };
  }, [sectionSettings, settingsLoaded]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image') {
          if (formData.image instanceof File) {
            submitData.append('image', formData.image);
          }
        } else if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      if (editingService) {
        const res = await api.put(`/services/${editingService.id}/`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setServices(services.map(s => s.id === editingService.id ? res.data : s));
      } else {
        submitData.append('order', services.length);
        const res = await api.post('/services/', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setServices([...services, res.data]);
      }
      updateLiveServices(editingService
        ? services.map(s => s.id === editingService.id ? formData : s)
        : [...services, formData]
      );
      resetForm();
    } catch (err) {
      console.error('Error saving service:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await api.delete(`/services/${id}/`);
      const updated = services.filter(s => s.id !== id);
      setServices(updated);
      updateLiveServices(updated);
    } catch (err) {
      console.error('Error deleting service:', err);
    }
  };

  const handleReorder = async (newOrder) => {
    setServices(newOrder);
    updateLiveServices(newOrder);
    try {
      await Promise.all(newOrder.map((service, index) =>
        api.put(`/services/${service.id}/`, { ...service, order: index })
      ));
    } catch (err) {
      console.error('Error reordering:', err);
    }
  };

  const resetForm = () => {
    setFormData(emptyService);
    setEditingService(null);
    setIsCreating(false);
    setImagePreview(null);
    // Scroll back to top when closing the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEdit = (service) => {
    setFormData(service);
    setEditingService(service);
    setIsCreating(true);
    setImagePreview(service.image ? buildMediaUrl(service.image) : null);
    // Scroll to top to keep header visible
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getIconComponent = (iconName) => {
    const found = ICON_OPTIONS.find(i => i.name === iconName);
    return found ? found.icon : Code;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div style={{ width: '3rem', height: '3rem', border: `4px solid ${theme.bgTertiary}`, borderTopColor: theme.accent, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '64rem', margin: '0 auto', padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: theme.accent, borderRadius: '1rem' }}>
            <Package style={{ width: '1.5rem', height: '1.5rem', color: '#fff' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: theme.textPrimary, margin: 0 }}>Services</h1>
            <p style={{ color: theme.textSecondary, margin: 0, fontSize: '0.875rem' }}>Showcase what you offer</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowSettings(!showSettings)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.75rem 1rem', backgroundColor: showSettings ? theme.accent : theme.bgSecondary,
              color: showSettings ? '#fff' : theme.textSecondary, border: `1px solid ${theme.border}`,
              borderRadius: '0.75rem', cursor: 'pointer',
            }}
          >
            <Settings size={18} /> Display Settings
          </motion.button>
          {!isCreating && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsCreating(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.25rem', backgroundColor: theme.accent,
                color: '#fff', border: 'none', borderRadius: '0.75rem',
                fontWeight: '600', cursor: 'pointer',
              }}
            >
              <Plus size={20} /> Add Service
            </motion.button>
          )}
        </div>
      </div>

      {/* Section Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ marginBottom: '1.5rem' }}
          >
            <div style={{
              backgroundColor: theme.bgSecondary, borderRadius: '1rem',
              padding: '1.5rem', border: `1px solid ${theme.accent}40`,
            }}>
              <h3 style={{ color: theme.textPrimary, margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600' }}>
                Display Settings
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {/* Section Title */}
                <div>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                    Section Title
                  </label>
                  <input
                    value={sectionSettings.title}
                    onChange={(e) => setSectionSettings({ ...sectionSettings, title: e.target.value })}
                    style={{
                      width: '100%', padding: '0.5rem', backgroundColor: theme.bgTertiary,
                      border: `1px solid ${theme.border}`, borderRadius: '0.375rem',
                      color: theme.textPrimary, fontSize: '0.875rem',
                    }}
                  />
                </div>

                {/* Cards Per Row */}
                <div>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                    Cards Per Row
                  </label>
                  <div style={{ display: 'flex', gap: '0.375rem' }}>
                    {[1, 2, 3, 4].map(num => (
                      <button
                        key={num}
                        onClick={() => setSectionSettings({ ...sectionSettings, cards_per_row: num })}
                        style={{
                          padding: '0.5rem 0.75rem', borderRadius: '0.375rem', cursor: 'pointer',
                          backgroundColor: sectionSettings.cards_per_row === num ? theme.accent : theme.bgTertiary,
                          border: `1px solid ${sectionSettings.cards_per_row === num ? theme.accent : theme.border}`,
                          color: sectionSettings.cards_per_row === num ? '#fff' : theme.textSecondary,
                          fontSize: '0.875rem',
                        }}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              {/* Toggle Options */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
                {[
                  { key: 'show_images', label: 'Show Images', icon: ImageIcon },
                  { key: 'show_icons', label: 'Show Icons', icon: Package },
                  { key: 'show_prices', label: 'Show Prices', icon: DollarSign },
                  { key: 'show_description', label: 'Show Description', icon: FileText },
                ].map(opt => (
                  <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={sectionSettings[opt.key]}
                      onChange={(e) => setSectionSettings({ ...sectionSettings, [opt.key]: e.target.checked })}
                      style={{ width: '1rem', height: '1rem', accentColor: theme.accent }}
                    />
                    <opt.icon size={14} style={{ color: theme.textSecondary }} />
                    <span style={{ color: theme.textPrimary, fontSize: '0.8125rem' }}>{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Section Background & Text Color */}
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
                <BackgroundPicker
                  value={sectionSettings.background_color}
                  onChange={(val) => setSectionSettings({ ...sectionSettings, background_color: val })}
                  label="Section Background"
                  showTextColor={true}
                  textColor={sectionSettings.text_color}
                  onTextColorChange={(val) => setSectionSettings({ ...sectionSettings, text_color: val })}
                />
              </div>

              {/* Card Background & Text Color */}
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
                <BackgroundPicker
                  value={sectionSettings.card_background_color}
                  onChange={(val) => setSectionSettings({ ...sectionSettings, card_background_color: val })}
                  label="Card Background"
                  showTextColor={true}
                  textColor={sectionSettings.card_text_color}
                  onTextColorChange={(val) => setSectionSettings({ ...sectionSettings, card_text_color: val })}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ marginBottom: '2rem' }}
          >
            <form onSubmit={handleSubmit} style={{
              backgroundColor: theme.bgSecondary, borderRadius: '1rem',
              padding: '1.5rem', border: `1px solid ${theme.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ color: theme.textPrimary, margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                  {editingService ? 'Edit Service' : 'New Service'}
                </h2>
                <button type="button" onClick={resetForm} style={{
                  background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer',
                }}>
                  <X size={20} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {/* Title */}
                <div>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Service Title *
                  </label>
                  <input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Web Development"
                    style={{
                      width: '100%', padding: '0.75rem', backgroundColor: theme.bgTertiary,
                      border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
                      color: theme.textPrimary, fontSize: '0.875rem',
                    }}
                  />
                </div>

                {/* Icon */}
                <div>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Icon
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {ICON_OPTIONS.map(({ name, icon: Icon }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: name })}
                        style={{
                          padding: '0.5rem', borderRadius: '0.5rem', cursor: 'pointer',
                          backgroundColor: formData.icon === name ? theme.accent : theme.bgTertiary,
                          border: `1px solid ${formData.icon === name ? theme.accent : theme.border}`,
                          color: formData.icon === name ? '#fff' : theme.textSecondary,
                        }}
                        title={name}
                      >
                        <Icon size={18} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image Upload */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Service Image (optional)
                  </label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    {imagePreview ? (
                      <div style={{ position: 'relative' }}>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '0.5rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData({ ...formData, image: null });
                          }}
                          style={{
                            position: 'absolute', top: '-8px', right: '-8px',
                            padding: '0.25rem', backgroundColor: theme.error, border: 'none',
                            borderRadius: '50%', color: '#fff', cursor: 'pointer',
                          }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        width: '120px', height: '80px', border: `2px dashed ${theme.border}`,
                        borderRadius: '0.5rem', cursor: 'pointer', backgroundColor: theme.bgTertiary,
                      }}>
                        <input type="file" accept="image/*" onChange={handleImageSelect} style={{ display: 'none' }} />
                        <Upload size={20} style={{ color: theme.textSecondary }} />
                        <span style={{ color: theme.textSecondary, fontSize: '0.75rem', marginTop: '0.25rem' }}>Upload</span>
                      </label>
                    )}
                    <p style={{ color: theme.textSecondary, fontSize: '0.75rem', margin: 0 }}>
                      Add an image to make your service stand out. Recommended: 800x600px
                    </p>
                  </div>
                </div>

                {/* Short Description */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Short Description (for cards)
                  </label>
                  <input
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    placeholder="Brief one-liner about this service"
                    maxLength={300}
                    style={{
                      width: '100%', padding: '0.75rem', backgroundColor: theme.bgTertiary,
                      border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
                      color: theme.textPrimary, fontSize: '0.875rem',
                    }}
                  />
                </div>

                {/* Full Description */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Full Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of what this service includes..."
                    rows={3}
                    style={{
                      width: '100%', padding: '0.75rem', backgroundColor: theme.bgTertiary,
                      border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
                      color: theme.textPrimary, fontSize: '0.875rem', resize: 'vertical',
                    }}
                  />
                </div>

                {/* Price Type */}
                <div>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Pricing Type
                  </label>
                  <select
                    value={formData.price_type}
                    onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
                    style={{
                      width: '100%', padding: '0.75rem', backgroundColor: theme.bgTertiary,
                      border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
                      color: theme.textPrimary, fontSize: '0.875rem',
                    }}
                  >
                    {PRICE_TYPES.map(pt => (
                      <option key={pt.value} value={pt.value}>{pt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Price {formData.price_type !== 'contact' && '*'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <DollarSign size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: theme.textSecondary }} />
                    <input
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder={formData.price_type === 'contact' ? 'Optional' : 'e.g., 50'}
                      disabled={formData.price_type === 'contact'}
                      style={{
                        width: '100%', padding: '0.75rem', paddingLeft: '2.5rem',
                        backgroundColor: theme.bgTertiary,
                        border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
                        color: theme.textPrimary, fontSize: '0.875rem',
                        opacity: formData.price_type === 'contact' ? 0.5 : 1,
                      }}
                    />
                  </div>
                </div>

                {/* Toggles */}
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: '2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      style={{ width: '1rem', height: '1rem', accentColor: theme.accent }}
                    />
                    <Star size={16} style={{ color: theme.warning }} />
                    <span style={{ color: theme.textPrimary, fontSize: '0.875rem' }}>Featured Service</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: '1rem', height: '1rem', accentColor: theme.accent }}
                    />
                    <span style={{ color: theme.textPrimary, fontSize: '0.875rem' }}>Active (visible on profile)</span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '0.75rem 1.5rem', backgroundColor: 'transparent',
                    border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
                    color: theme.textSecondary, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.75rem 1.5rem', backgroundColor: theme.success,
                    border: 'none', borderRadius: '0.5rem',
                    color: '#fff', fontWeight: '600', cursor: 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : (editingService ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Services List - Hidden when editing to reduce clutter */}
      {!isCreating && services.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '4rem 2rem',
          backgroundColor: theme.bgSecondary, borderRadius: '1rem',
          border: `1px dashed ${theme.border}`,
        }}>
          <Package size={48} style={{ color: theme.textTertiary, marginBottom: '1rem' }} />
          <p style={{ color: theme.textSecondary, margin: 0 }}>No services yet. Add your first service!</p>
        </div>
      ) : !isCreating && (
        <Reorder.Group axis="y" values={services} onReorder={handleReorder} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {services.map((service) => {
            const IconComponent = getIconComponent(service.icon);
            const priceType = PRICE_TYPES.find(pt => pt.value === service.price_type);
            const isExpanded = expandedId === service.id;
            return (
              <Reorder.Item key={service.id} value={service} style={{ marginBottom: '0.75rem' }}>
                <motion.div
                  layout
                  whileHover={{ scale: 1.005 }}
                  style={{
                    backgroundColor: theme.bgSecondary,
                    borderRadius: '1rem',
                    border: `1px solid ${isExpanded ? theme.accent : theme.border}`,
                    opacity: service.is_active ? 1 : 0.5,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {/* Main Row */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '1rem 1.25rem',
                  }}>
                    {/* Drag Handle */}
                    <div style={{
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      backgroundColor: theme.bgTertiary + '50',
                      cursor: 'grab',
                    }}>
                      <GripVertical size={16} style={{ color: theme.textTertiary }} />
                    </div>

                    {/* Image or Icon */}
                    {service.image ? (
                      <div style={{
                        width: '56px', height: '56px',
                        borderRadius: '0.75rem',
                        overflow: 'hidden',
                        border: `2px solid ${theme.border}`,
                        flexShrink: 0,
                      }}>
                        <img
                          src={buildMediaUrl(service.image)}
                          alt={service.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        width: '56px', height: '56px',
                        background: `linear-gradient(135deg, ${theme.accent}30 0%, ${theme.accent}10 100%)`,
                        borderRadius: '0.75rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `1px solid ${theme.accent}30`,
                        flexShrink: 0,
                      }}>
                        <IconComponent size={24} style={{ color: theme.accent }} />
                      </div>
                    )}

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : service.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <h3 style={{
                          color: theme.textPrimary, margin: 0,
                          fontSize: '0.9375rem', fontWeight: '600',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {service.title}
                        </h3>
                        {service.is_featured && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            padding: '0.125rem 0.5rem',
                            backgroundColor: theme.warning + '20',
                            borderRadius: '1rem',
                            fontSize: '0.6875rem',
                            color: theme.warning,
                            fontWeight: '600',
                          }}>
                            <Star size={10} style={{ fill: theme.warning }} /> Featured
                          </span>
                        )}
                        {!service.is_active && (
                          <span style={{
                            fontSize: '0.6875rem', color: theme.textTertiary,
                            padding: '0.125rem 0.5rem',
                            backgroundColor: theme.bgTertiary,
                            borderRadius: '1rem',
                          }}>
                            Hidden
                          </span>
                        )}
                      </div>
                      <p style={{
                        color: theme.textSecondary, margin: '0.375rem 0 0',
                        fontSize: '0.8125rem', lineHeight: '1.4',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {service.short_description || service.description || 'No description'}
                      </p>
                    </div>

                    {/* Price Badge */}
                    <div style={{
                      padding: '0.5rem 0.875rem',
                      backgroundColor: service.price_type === 'contact' ? theme.accent + '15' : theme.success + '15',
                      borderRadius: '0.5rem',
                      textAlign: 'center',
                      minWidth: '80px',
                    }}>
                      {service.price_type === 'contact' ? (
                        <span style={{ color: theme.accent, fontSize: '0.8125rem', fontWeight: '600' }}>Quote</span>
                      ) : service.price ? (
                        <span style={{ color: theme.success, fontSize: '0.9375rem', fontWeight: '700' }}>
                          ${service.price}
                        </span>
                      ) : (
                        <span style={{ color: theme.textTertiary, fontSize: '0.75rem' }}>—</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : service.id)}
                        style={{
                          width: '36px', height: '36px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: isExpanded ? theme.accent + '20' : 'transparent',
                          border: `1px solid ${isExpanded ? theme.accent + '50' : theme.border}`,
                          borderRadius: '0.5rem',
                          color: isExpanded ? theme.accent : theme.textSecondary,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <button
                        onClick={() => startEdit(service)}
                        style={{
                          width: '36px', height: '36px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '0.5rem',
                          color: theme.textSecondary,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        style={{
                          width: '36px', height: '36px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: theme.error + '10',
                          border: `1px solid ${theme.error}30`,
                          borderRadius: '0.5rem',
                          color: theme.error,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={{
                          padding: '1rem 1.25rem',
                          borderTop: `1px solid ${theme.border}`,
                          backgroundColor: theme.bgTertiary + '40',
                        }}>
                          {/* Image Preview */}
                          {service.image && (
                            <div style={{ marginBottom: '1rem' }}>
                              <img
                                src={buildMediaUrl(service.image)}
                                alt={service.title}
                                style={{ maxWidth: '300px', borderRadius: '0.5rem' }}
                              />
                            </div>
                          )}

                          {/* Full Description */}
                          <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', color: theme.textSecondary, fontSize: '0.75rem', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Full Description
                            </label>
                            <p style={{ color: theme.textPrimary, margin: 0, fontSize: '0.9375rem', lineHeight: '1.6' }}>
                              {service.description || <span style={{ color: theme.textTertiary, fontStyle: 'italic' }}>No detailed description added. Click Edit to add one.</span>}
                            </p>
                          </div>

                          {/* Quick Edit Button */}
                          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
                            <button
                              onClick={() => startEdit(service)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                padding: '0.5rem 1rem', backgroundColor: theme.accent,
                                border: 'none', borderRadius: '0.5rem',
                                color: '#fff', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
                              }}
                            >
                              <Edit2 size={14} /> Edit Service Details
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      )}
    </div>
  );
};

export default AdminServices;
